import { Flex, Grid, Tag, Text } from "@chakra-ui/react";
import Button from "common/components/button";
import IconImage from "common/components/icon-image";
import FormatAsCurrency from "common/utils/formatAsCurrency";
import { getTerm } from "common/utils/getTerm";

const CostItem = ({title, amount, isTotal, padding}) => {
  return (
    <Flex className={`gap-2 justify-between w-full ${isTotal && 'font-medium'} ${padding || 'py-2'}`}>
      <Text className='text-base'>{title}</Text>
      <Text className='text-gray-500 text-base'>{amount}</Text>
    </Flex>
  )
}

function calculateSavings(property, deductible, lease, amount, upfrontAmount){
  
  let leaseBreakCost = 2 * lease?.monthlyRent
  if (property?.propertyDefaults?.terminationPolicy?.buyoutMonths) {
    leaseBreakCost = property?.propertyDefaults?.terminationPolicy?.buyoutMonths * lease?.monthlyRent
  }
  if (property?.propertyDefaults?.terminationPolicy?.penalty) {
    leaseBreakCost += property?.propertyDefaults?.terminationPolicy?.penalty
  }
  
  const leaseBreakMinCost = 2 * lease?.monthlyRent
  const leaseBreakMaxCost = 4 * lease?.monthlyRent

  const leaseTerm = lease?.term || getTerm(lease?.startDate, lease?.endDate)
  const totalMonthlyAmount = amount * leaseTerm
  const totalReLeaseCost = upfrontAmount + totalMonthlyAmount + deductible
  
  const savings = leaseBreakCost ? 
    FormatAsCurrency(leaseBreakCost - totalReLeaseCost, 'USD')
    // : `${FormatAsCurrency(leaseBreakMinCost - totalReLeaseCost)} - ${FormatAsCurrency(leaseBreakMaxCost - totalReLeaseCost)}`
    : `up to ${FormatAsCurrency(leaseBreakMaxCost - totalReLeaseCost, 'USD')}`

  return {
    amount,
    upfrontAmount,
    savings,
    leaseBreakCost,
    leaseBreakMinCost,
    leaseBreakMaxCost,
    totalReLeaseCost,
  }
}

const SavingsCalculator = ({ property, deductible, noticePeriod, lease, savings, id='savings-calculator', onSaveClick }) => {
  const leaseTerm = lease?.term || getTerm(lease?.startDate, lease?.endDate)

  return (
    <Flex className='flex-col gap-2' id={id}>
    <Grid className='p-6 grid-cols-1 lg:grid-cols-2 gap-8 border border-gray-200 rounded-lg bg-gray-50'>
      <Flex className='flex-col sm:flex-row gap-4 lg:col-span-2 sm:items-center justify-between'>
        <Text className='font-medium inline-flex gap-3'>
          <IconImage size='2xs' dark className='-mt-0.5' />
          Your savings{property?.name ? (` at ${property?.name} `) : (' ')}with ReLease
        </Text>
        {onSaveClick && (
          <Button
            title='Save Quote'
            wFull={false}
            className='w-fit'
            theme='secondary'
            onClick={onSaveClick}
          />
        )}
      </Flex>
      <Flex className='flex-col row-span-2 gap-4'>
        <Text className='font-medium text-base'>
          Costs without ReLease
        </Text>      
        <Flex className='flex-col divide-y divide-gray-200'>
          <CostItem
            title='Notice period'
            amount={`${property?.propertyDefaults?.terminationPolicy?.noticePeriod || '30-60'} days`}
          />
          <CostItem
            title='Early termination penalty'
            amount={<span>
              {property?.propertyDefaults?.terminationPolicy?.buyoutMonths || '2-4'} months <span className='hidden sm:inline'> rent</span>
            </span>}
          />
          <CostItem
            isTotal
            title='Total cost to move out early'
            amount={savings?.leaseBreakCost ? (
              FormatAsCurrency(savings?.leaseBreakCost, 'USD')
            ) : (
              `${FormatAsCurrency(savings?.leaseBreakMinCost, 'USD')} - ${FormatAsCurrency(savings?.leaseBreakMaxCost, 'USD')}`
            )}
          />
        </Flex>
      </Flex>
      <Flex className='flex-col gap-4'>
        <Text className='font-medium text-base'>
          Costs with ReLease
        </Text>
        <Flex className='flex-col divide-y divide-gray-200'>
          <CostItem
            title='Notice period'
            amount={`${noticePeriod || 3} days`}
          />
          <Flex className='flex-col'>
          {savings?.upfrontAmount && (
            <CostItem
              title={
                <span>
                  ReLease fees<span className="text-gray-500"> (paid upfront)</span>
                </span>
              }
              amount={FormatAsCurrency(savings?.upfrontAmount, 'USD')}
            />
          )}
          {savings?.amount && (
            <CostItem
              title={
                <span>
                  ReLease fees<span className="text-gray-500"> (monthly installments)</span>
                </span>
              }
              amount={`${FormatAsCurrency(savings?.amount * leaseTerm, 'USD')}`}
            />
          )}
          </Flex>
          <CostItem
            title={
              <span>
                Deductible<span className="text-gray-500"> (due at time of claim)</span>
              </span>
            }
            amount={FormatAsCurrency(deductible, 'USD')}
          />
          <CostItem
            isTotal
            title={`Total cost to move out early`}
            amount={FormatAsCurrency(savings?.totalReLeaseCost, 'USD')}
          />
        </Flex>
      </Flex>
      <Flex className='flex-col gap-4 -mt-4 -ml-2 -mr-3'>
        <Tag 
          colorScheme='green' size='sm' className='w-full px-3 flex gap-2 cursor-pointer'
        >
        <CostItem
          isTotal
          padding='pt-2 pb-1.5'
          title={
            <span className='inline-flex gap-2'>
              <IconImage size='3xs' className='-mt-[1px]' dark />
              <span>
                Your savings<span className='hidden sm:inline'> with ReLease</span>
              </span>
            </span>
          }
          amount={
            <span className='inline-block first-letter:capitalize text-green-800'>              
              {savings?.savings}
            </span>
          }
        />
        </Tag>        
      </Flex>
    </Grid>
    <Text className='px-2 text-sm text-gray-500'>
      The lease termination cost shown is an estimate. For exact fees and terms, please refer to your lease agreement or contact your property manager or landlord directly.
    </Text>
    </Flex>
  )
}

export {
  SavingsCalculator,
  calculateSavings
}